import React, { useState, useRef, useEffect, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import Checkbox from 'components/V2/Checkbox';
import PropTypes from 'prop-types';
import {
    formatIdentityProofData,
    formatAdditionalChecksData,
    returnEnabledCountries,
    formatPreIdentityChecksData,
    shouldShowCriminalCheckFallback,
} from 'views/V2/Integration/utils';

import styles from './workflowPreview.module.css';

function WorkflowPreview({
    workflowData,
    className,
    isEditBrandForm,
    sevenYearCriminalCheckFallback,
    onFallbackChange,
}) {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const preIdentityChecks = formatPreIdentityChecksData(workflowData?.pre_identity_checks);
    const identityProof = formatIdentityProofData(workflowData?.id_verifications);
    const additionalChecks = formatAdditionalChecksData(workflowData?.additional_checks);
    const allowedCountries = returnEnabledCountries(
        workflowData?.workflow?.enabled_countries || workflowData?.enabled_countries
    );

    const containerRef = useRef(null);

    const checkOverflow = useCallback(() => {
        if (containerRef.current) {
            const isOverflowing =
                containerRef.current.scrollWidth > containerRef.current.clientWidth;
            setIsOverflowing(isOverflowing);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [allowedCountries, checkOverflow]);

    const showFallback = shouldShowCriminalCheckFallback(workflowData);

    const preIdentityChecksText = [preIdentityChecks, identityProof?.quickVerify]
        .filter(Boolean)
        .join(', ');

    return (
        <Box className={clsx(className, styles.workflowPreviewContainer)}>
            <Box className={styles.workflowConfigBox}>
                <Typography className={styles.configHeading}>Countries Allowed:</Typography>
                <Box className={styles.moreWrapper}>
                    <p
                        className={clsx(styles.configDesc, styles.countryListText)}
                        ref={containerRef}
                    >
                        {allowedCountries}
                    </p>
                    {isOverflowing && (
                        <Tooltip
                            title={isOverflowing ? allowedCountries : ''}
                            arrow
                            placement="bottom"
                        >
                            <span> more...</span>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {preIdentityChecks?.length > 1 && (
                <Box className={styles.workflowConfigBox}>
                    <Typography className={styles.configHeading}>
                        Checks - 2FA & Risk Assessment{' '}
                    </Typography>
                    <Typography className={styles.configDesc}>{preIdentityChecksText}</Typography>
                </Box>
            )}
            <Box className={styles.workflowConfigBox}>
                <Typography className={styles.configHeading}>Identity Proof: </Typography>
                <Typography className={styles.configDesc}>
                    {identityProof?.verificationList.map((checks, index) => {
                        return (
                            <span key={checks}>
                                {index !== 0 && (
                                    <strong>{identityProof?.verificationConfig}</strong>
                                )}
                                {checks}
                            </span>
                        );
                    })}
                </Typography>
            </Box>
            {additionalChecks?.length > 1 && (
                <Box className={styles.workflowConfigBox}>
                    <Typography className={styles.configHeading}>Additional Checks: </Typography>
                    <Typography className={styles.configDesc}>{additionalChecks}</Typography>
                </Box>
            )}

            {isEditBrandForm && showFallback && (
                <Box className={styles.workflowConfigBox}>
                    <Typography className={styles.fallbackConfigHeading}>
                        Criminal Check Fallback
                        <Tooltip
                            title="When your workflow includes countries outside the US, the 7-Year Criminal Check cannot be run for those users. Enable this to automatically run a Global Watchlist check for non-US users as an alternative criminal screening."
                            arrow
                            placement="right"
                            className={styles.tooltip}
                        />
                    </Typography>
                    <Box className={styles.fallbackConfigDescBox}>
                        <Checkbox
                            checked={sevenYearCriminalCheckFallback === 'global_watchlist'}
                            size="normal"
                            onChange={(e) =>
                                onFallbackChange(e.target.checked ? 'global_watchlist' : null)
                            }
                        />
                        <Typography className={styles.configDesc}>
                            Use Global watchlist check as Fallback for Non-US Users
                        </Typography>
                    </Box>
                </Box>
            )}

            {!isEditBrandForm && showFallback && (
                <Box className={styles.workflowConfigBox}>
                    <Typography className={styles.configHeading}>
                        Criminal Check Fallback:{' '}
                        <span>
                            {sevenYearCriminalCheckFallback === 'global_watchlist'
                                ? 'Enabled'
                                : 'Disabled'}
                        </span>
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

WorkflowPreview.propTypes = {
    workflowData: PropTypes.object,
    className: PropTypes.string,
    isEditBrandForm: PropTypes.bool,
    sevenYearCriminalCheckFallback: PropTypes.string,
    onFallbackChange: PropTypes.func,
};

export default WorkflowPreview;
